import React, { useEffect, useMemo } from 'react';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import {
  GetSiblingProductFragment,
  GetSiblingProductsQuery,
  GetSiblingProductsQueryVariables,
} from 'src/generated/shopify-graphql';
import { prependLocaleToPath } from 'src/scripts/utils/localization';
import { notNil } from 'src/scripts/utils/typescriptUtils';
import { sortBy } from 'lodash-es';
import { IColorwayProductData } from '../../types/PDP';
import { ProductContext } from '../../ProductContext';
import './SiblingProductsToggle.scss';

const GetSiblingProductsQueryLoader = loader('./GetSiblingProducts.shopify.graphql');

export enum ProductSiblingToggleType {
  SIZE = 'size',
  LENGTH = 'length',
}

const SiblingProductToggleRefreshSolution = ({
  siblingsIdentifierTag,
  currentColor,
  currentHandle,
}: {
  siblingsIdentifierTag: string;
  currentColor: string;
  // for the refresh solution to find the "matching product" to get the length value
  currentHandle: string;
}) => {
  const { setSiblingProductToggleType } = React.useContext(ProductContext);
  const { data, loading, error } = useQuery<GetSiblingProductsQuery, GetSiblingProductsQueryVariables>(
    GetSiblingProductsQueryLoader,
    { variables: { siblingIdQuery: `tag:'${siblingsIdentifierTag}'` } }
  );

  const filteredSiblingProducts = useMemo(() => {
    if (!data || loading || error || !currentColor) return [];
    return filterProductsByColor(data, currentColor);
  }, [data, loading, error, currentColor]);

  const siblingType: ProductSiblingToggleType | undefined = useMemo(
    () => getProductSiblingType(filteredSiblingProducts),
    [filteredSiblingProducts]
  );

  const siblingProductOptions = useMemo(() => {
    return computeToggleOptions(filteredSiblingProducts, siblingType);
  }, [filteredSiblingProducts, siblingType]);

  useEffect(() => {
    // set the toggle type only if there are more than 1 option to toggle between, means toggle button are rendered
    setSiblingProductToggleType(siblingProductOptions.length > 1 ? siblingType : undefined);
  }, [setSiblingProductToggleType, siblingType, siblingProductOptions]);

  const currentSiblingProductOption = useMemo(
    () => siblingProductOptions.find((item) => item.handle === currentHandle),
    [siblingProductOptions, currentHandle]
  );

  const currentInseamValue = currentSiblingProductOption?.inseamLabel;
  const selectedButtonLabel = currentSiblingProductOption?.buttonLabel;

  // don't render the toggle if there is only 1 or no option to toggle between or if we couldn't determine the sibling type
  if (!(siblingProductOptions.length > 1) || !siblingType) return null;
  return (
    <div id="SiblingProductLinksPDP">
      <p className="body">
        <span className="body bold mr-2">{siblingType === 'size' ? 'Size:' : 'Length:'}</span>
        {siblingType === 'size' ? selectedButtonLabel : currentInseamValue ? `${currentInseamValue} Inseam` : null}
      </p>
      <div className="sibling-toggle-buttons">
        {siblingProductOptions.map((item) => {
          const active = item.handle === currentHandle;
          return (
            <a
              href={item.href}
              key={item.handle}
              className={`sibling-button button-toggle link-styled-as-button ${active ? 'active' : ''}`}
              style={{
                textDecoration: 'none',
                pointerEvents: active ? 'none' : 'auto',
              }}
              aria-disabled={active}
            >
              {item.buttonLabel}
            </a>
          );
        })}
      </div>
    </div>
  );
};

export const SiblingProductToggle = SiblingProductToggleRefreshSolution;

// helper components
export const SiblingProductLinksPDP = ({ colorwayProductData }: { colorwayProductData: IColorwayProductData }) => {
  const { tags, colorName, handle } = colorwayProductData;
  const siblingsIdentifierTag = tags?.find((tag) => tag.toLowerCase().startsWith(SiblingsTagPrefix));

  if (!siblingsIdentifierTag) return null;
  return (
    <SiblingProductToggleRefreshSolution
      siblingsIdentifierTag={siblingsIdentifierTag}
      currentColor={colorName}
      currentHandle={handle}
    />
  );
};

// helpers
const getProductSiblingType = (products: GetSiblingProductFragment[]): ProductSiblingToggleType | undefined => {
  if (!products.length) return undefined;
  let lengthCount = 0;
  let sizeCount = 0;
  for (const product of products) {
    const optionNames = product.options.map((option) => option.name.toLowerCase());
    const optionValues = product.options.flatMap((option) => (option.values ?? []).map((v) => v.toLowerCase()));
    if (optionNames.includes('length')) lengthCount++;
    if (optionNames.includes('size') && !optionValues.includes('one size')) sizeCount++;
  }
  if (lengthCount > 1) return ProductSiblingToggleType.LENGTH;
  if (sizeCount > 1) return ProductSiblingToggleType.SIZE;
  return undefined;
};

const filterProductsByColor = (data: GetSiblingProductsQuery, currentColor: string) => {
  return data.products.edges
    .map((edge) => edge.node)
    .filter((product) => {
      const colorOption = product.options.find((option) => option.name.toLowerCase() === 'color');
      return colorOption?.values.includes(currentColor) && product.onlineStoreUrl;
    });
};

const computeToggleOptions = (
  products: GetSiblingProductFragment[],
  siblingType: ProductSiblingToggleType = ProductSiblingToggleType.LENGTH
): SiblingToggleOption[] => {
  const siblingOptions = products
    .map((product) => {
      const option = product.options.find((opt) => opt.name.toLowerCase() === siblingType);
      const value = option?.values?.[0];
      // Ignore if siblingType is SIZE and value is "One Size"
      if (siblingType === ProductSiblingToggleType.SIZE && value?.toLowerCase() === 'one size') {
        return null;
      }
      const { fit, inseam } = value ? destructureFitAndInseam(value) : { fit: null, inseam: null };
      return value && fit
        ? {
            inseamLabel: inseam,
            buttonLabel: fit,
            href: prependLocaleToPath(`/products/` + product.handle),
            handle: product.handle,
          }
        : null;
    })
    .filter(notNil);

  // sort the toggle options based on the preferred order defined in meta-fields. meta-field name might be confusing but it is what it is
  const combinedSortOrder = window?.server_pdp_data?.metaFields?.lengthToggleSortorder || [];
  const preferredSortOrder = combinedSortOrder.map((fit) => fit.toLowerCase());
  const sortedSiblingOptions = sortBy(siblingOptions, (option) =>
    preferredSortOrder.indexOf(option.buttonLabel.toLowerCase())
  );

  return sortedSiblingOptions;
};

type SiblingToggleOption = {
  inseamLabel: string | undefined;
  buttonLabel: string;
  href: string;
  handle: string;
};

export const destructureFitAndInseam = (lengthValue: string) => {
  // value comes back in format `Long 33"` or `Full-length` or `Cropped`
  const lastSpaceIndex = lengthValue.lastIndexOf(' ');
  const inseam = lengthValue.substring(lastSpaceIndex + 1);
  if (inseam.includes('"')) {
    const fit = lengthValue.substring(0, lastSpaceIndex);
    return { fit, inseam };
  }
  return { fit: lengthValue, inseam: undefined };
};

const SiblingsTagPrefix = 'sibling_product_identifier:';
